import React, { useEffect, useState, useCallback, useRef } from "react";
import { Plus, Trash2, Edit3, Save, Send, Download, Eye, ArrowLeft, FileText, Copy, Check } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * ContractsAdmin — admin-only. Two subviews (tabs inside this tab):
 *   1) Templates  — reusable HTML contract bodies + default flag
 *   2) Contracts  — per-client instances, view/send/download PDF/audit
 * Not linked from any public page; only admins with a JWT can reach these endpoints.
 */
export default function ContractsAdmin() {
  const [view, setView] = useState("contracts"); // "contracts" | "templates"

  return (
    <div data-testid="contracts-admin">
      <div className="flex gap-2 mb-6 border-b border-white/10">
        <button onClick={() => setView("contracts")}
          className={`px-4 py-2 text-xs tracking-widest uppercase transition ${view === "contracts" ? "text-[#D4AF37] border-b-2 border-[#D4AF37]" : "text-white/50 hover:text-white"}`}
          data-testid="contracts-view-list">Client Contracts</button>
        <button onClick={() => setView("templates")}
          className={`px-4 py-2 text-xs tracking-widest uppercase transition ${view === "templates" ? "text-[#D4AF37] border-b-2 border-[#D4AF37]" : "text-white/50 hover:text-white"}`}
          data-testid="contracts-view-templates">Templates</button>
      </div>
      {view === "contracts" ? <ContractsList /> : <TemplatesList />}
    </div>
  );
}

/* -------------------- Templates -------------------- */
function TemplatesList() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try { const r = await api.get("/admin/contract-templates"); setRows(r.data); }
    catch { toast.error("Could not load templates"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => setEditing({ name: "", body_html: "", is_default: rows.length === 0 });
  const setDefault = async (t) => {
    await api.post(`/admin/contract-templates/${t.id}/set-default`);
    toast.success("Set as default"); load();
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? Existing signed contracts are unaffected.`)) return;
    await api.delete(`/admin/contract-templates/${t.id}`); toast.success("Deleted"); load();
  };

  if (editing) return <TemplateEditor initial={editing} onCancel={() => setEditing(null)}
                                       onSaved={() => { setEditing(null); load(); }} />;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div>
          <div className="eyebrow">Contract Templates</div>
          <div className="text-sm text-white/60 mt-1">{rows.length} template{rows.length === 1 ? "" : "s"}. The default template is used when auto-creating contracts on status → Contacted.</div>
        </div>
        <button onClick={openNew} className="btn-gold text-xs" data-testid="templates-add">
          <Plus size={12}/> New template
        </button>
      </div>
      <div className="space-y-3" data-testid="templates-list">
        {rows.map(t => (
          <div key={t.id} className="bg-[#0a0a0a] border border-white/10 p-5 flex items-start gap-4" data-testid={`template-${t.id}`}>
            <FileText size={22} className="text-[#D4AF37] mt-1 shrink-0"/>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <div className="font-serif text-lg">{t.name || "(untitled)"}</div>
                {t.is_default && <span className="text-[0.55rem] tracking-widest uppercase text-[#D4AF37] border border-[#D4AF37]/40 px-2 py-0.5">Default</span>}
              </div>
              <div className="text-xs text-white/40 mt-1">{stripHtml(t.body_html).slice(0, 180)}{stripHtml(t.body_html).length > 180 ? "…" : ""}</div>
            </div>
            <div className="flex flex-col gap-1 items-end">
              <button onClick={() => setEditing({ ...t })} className="btn-ghost text-xs" data-testid={`template-${t.id}-edit`}><Edit3 size={12}/> Edit</button>
              {!t.is_default && <button onClick={() => setDefault(t)} className="btn-ghost text-xs" data-testid={`template-${t.id}-set-default`}>Set default</button>}
              <button onClick={() => remove(t)} className="btn-ghost text-xs text-red-300 hover:text-red-500 border-red-500/40" data-testid={`template-${t.id}-delete`}><Trash2 size={12}/> Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TemplateEditor({ initial, onCancel, onSaved }) {
  const [row, setRow] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  const save = async () => {
    if (!row.name?.trim()) { toast.error("Give the template a name"); return; }
    setBusy(true);
    try {
      const payload = { name: row.name, body_html: row.body_html || "", is_default: !!row.is_default };
      if (row.id) await api.put(`/admin/contract-templates/${row.id}`, payload);
      else await api.post(`/admin/contract-templates`, payload);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="template-editor">
      <button onClick={onCancel} className="btn-ghost text-xs"><ArrowLeft size={12}/> Back to templates</button>
      <label className="block">
        <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">Name</span>
        <input value={row.name || ""} onChange={e => setRow({ ...row, name: e.target.value })}
          className="mt-1 w-full bg-black border border-white/15 px-3 py-2 text-sm focus:border-[#D4AF37] outline-none"
          data-testid="template-name" />
      </label>
      <label className="inline-flex items-center gap-2 text-sm">
        <input type="checkbox" checked={!!row.is_default} onChange={e => setRow({ ...row, is_default: e.target.checked })}
          className="accent-[#D4AF37]" data-testid="template-default"/>
        <span className="text-white/70">Use as default template (auto-attached when a lead moves to "Contacted")</span>
      </label>
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-[0.6rem] tracking-[0.25em] uppercase text-white/60">Contract body</span>
          <div className="flex gap-2 text-xs">
            <button onClick={() => setPreviewMode(false)} className={`btn-ghost ${!previewMode ? "border-[#D4AF37] text-[#D4AF37]" : ""}`} data-testid="template-edit-mode">HTML</button>
            <button onClick={() => setPreviewMode(true)} className={`btn-ghost ${previewMode ? "border-[#D4AF37] text-[#D4AF37]" : ""}`} data-testid="template-preview-mode">Preview</button>
          </div>
        </div>
        {previewMode ? (
          <div className="bg-white text-black p-8 prose max-h-[500px] overflow-y-auto" data-testid="template-preview"
               dangerouslySetInnerHTML={{ __html: row.body_html || "" }} />
        ) : (
          <textarea rows={20} value={row.body_html || ""}
            onChange={e => setRow({ ...row, body_html: e.target.value })}
            className="w-full bg-black border border-white/15 px-3 py-3 text-sm font-mono focus:border-[#D4AF37] outline-none"
            placeholder="Use HTML: <h1>Title</h1>  <h2>Section</h2>  <p>Paragraph…</p>  <strong>Bold</strong>"
            data-testid="template-body" />
        )}
      </div>
      <div className="flex gap-2">
        <button onClick={save} disabled={busy} className="btn-gold" data-testid="template-save">
          <Save size={14}/> {row.id ? "Save changes" : "Create template"}
        </button>
        <button onClick={onCancel} className="btn-ghost">Cancel</button>
      </div>
    </div>
  );
}

/* -------------------- Contracts list + detail -------------------- */
function ContractsList() {
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    try { const r = await api.get("/admin/contracts"); setRows(r.data); }
    catch { toast.error("Could not load contracts"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const newFromLead = async () => {
    const email = window.prompt("Client email:");
    if (!email) return;
    const name = window.prompt("Client name:") || "";
    try {
      const r = await api.post("/admin/contracts", { lead_name: name, lead_email: email });
      toast.success("Contract created"); setDetail(r.data); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (detail) return <ContractDetail cid={detail.id} onBack={() => { setDetail(null); load(); }} />;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div>
          <div className="eyebrow">Client Contracts</div>
          <div className="text-sm text-white/60 mt-1">{rows.length} contract{rows.length === 1 ? "" : "s"}. Contracts are auto-created when a lead is set to "Contacted".</div>
        </div>
        <button onClick={newFromLead} className="btn-gold text-xs" data-testid="contracts-new">
          <Plus size={12}/> New contract
        </button>
      </div>
      <div className="overflow-x-auto" data-testid="contracts-list">
        <table className="w-full text-sm">
          <thead className="text-[0.6rem] tracking-widest uppercase text-white/40">
            <tr><th className="text-left py-2 px-3">Client</th><th className="text-left px-3">Email</th><th className="text-left px-3">Status</th><th className="text-left px-3">Sent</th><th className="text-left px-3">Signed</th><th className="text-left px-3"></th></tr>
          </thead>
          <tbody>
            {rows.map(c => (
              <tr key={c.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`contract-row-${c.id}`}>
                <td className="py-3 px-3 text-white/90">{c.lead_name || "—"}</td>
                <td className="px-3 text-white/60">{c.lead_email || "—"}</td>
                <td className="px-3"><StatusBadge status={c.status}/></td>
                <td className="px-3 text-white/40 text-xs">{c.sent_at ? new Date(c.sent_at).toLocaleDateString() : "—"}</td>
                <td className="px-3 text-white/40 text-xs">{c.signed_at ? new Date(c.signed_at).toLocaleDateString() : "—"}</td>
                <td className="px-3 text-right">
                  <button onClick={() => setDetail(c)} className="btn-ghost text-xs" data-testid={`contract-${c.id}-open`}>Open</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <div className="text-center text-white/30 py-16">No contracts yet.</div>}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const cfg = {
    draft: ["Draft", "text-white/50 border-white/20"],
    sent: ["Sent", "text-blue-300 border-blue-500/40"],
    viewed: ["Viewed", "text-yellow-200 border-yellow-500/40"],
    signed: ["Signed", "text-[#D4AF37] border-[#D4AF37]/60"],
    booked: ["Booked", "text-green-300 border-green-500/60"],
  };
  const [label, cls] = cfg[status] || [status, "text-white/50 border-white/20"];
  return <span className={`text-[0.55rem] tracking-widest uppercase border px-2 py-0.5 ${cls}`}>{label}</span>;
}

function ContractDetail({ cid, onBack }) {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const load = useCallback(async () => {
    const r = await api.get(`/admin/contracts/${cid}`); setC(r.data);
  }, [cid]);
  useEffect(() => { load(); }, [load]);

  if (!c) return <div className="text-white/40">Loading…</div>;
  const link = `${window.location.origin}/contracts/${c.token}`;

  const sendLink = async () => {
    if (!c.lead_email) { toast.error("Client email is missing"); return; }
    setBusy(true);
    try { await api.post(`/admin/contracts/${c.id}/send`); toast.success(`Sent to ${c.lead_email}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to send"); }
    finally { setBusy(false); }
  };
  const downloadPdf = async () => {
    try {
      const r = await api.get(`/admin/contracts/${c.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `contract-${c.id}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error("PDF download failed"); }
  };
  const copyLink = () => { navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  const remove = async () => {
    if (!window.confirm("Delete this contract? Cannot be undone.")) return;
    await api.delete(`/admin/contracts/${c.id}`); toast.success("Deleted"); onBack();
  };

  return (
    <div className="space-y-6" data-testid="contract-detail">
      <button onClick={onBack} className="btn-ghost text-xs"><ArrowLeft size={12}/> Back to list</button>

      <div className="bg-[#0a0a0a] border border-white/10 p-6">
        <div className="flex flex-wrap justify-between gap-4 mb-4">
          <div>
            <div className="font-serif text-2xl">{c.lead_name || "(no name)"}</div>
            <div className="text-sm text-white/60">{c.lead_email || "(no email)"}</div>
            <div className="mt-3"><StatusBadge status={c.status}/></div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={copyLink} className="btn-ghost text-xs" data-testid="contract-copy-link">
              {copied ? <Check size={12}/> : <Copy size={12}/>} {copied ? "Copied" : "Copy link"}
            </button>
            <button onClick={sendLink} disabled={busy || !c.lead_email} className="btn-ghost text-xs" data-testid="contract-send">
              <Send size={12}/> Email link
            </button>
            {c.signed_at && (
              <button onClick={downloadPdf} className="btn-gold text-xs" data-testid="contract-download-pdf">
                <Download size={12}/> Download PDF
              </button>
            )}
            <a href={link} target="_blank" rel="noreferrer" className="btn-ghost text-xs" data-testid="contract-preview">
              <Eye size={12}/> Preview as client
            </a>
            <button onClick={remove} className="btn-ghost text-xs text-red-300 hover:text-red-500 border-red-500/40" data-testid="contract-delete">
              <Trash2 size={12}/> Delete
            </button>
          </div>
        </div>
        <div className="text-[0.65rem] uppercase tracking-widest text-white/40 mb-1">Public link</div>
        <div className="bg-black border border-white/10 px-3 py-2 text-xs font-mono break-all text-white/70">{link}</div>
      </div>

      {c.signed_at && c.client_data && (
        <div className="bg-[#0a0a0a] border border-white/10 p-6">
          <div className="eyebrow mb-3">Client-submitted details</div>
          <dl className="grid sm:grid-cols-2 gap-3 text-sm">
            {Object.entries(c.client_data).map(([k, v]) => (
              <div key={k}>
                <dt className="text-[0.6rem] tracking-widest uppercase text-white/40">{k.replace(/_/g, " ")}</dt>
                <dd className="text-white/85">{v || "—"}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-4 pt-4 border-t border-white/10 text-xs text-white/60">
            Signed by <strong className="text-[#D4AF37]">{c.signature_name}</strong> on {new Date(c.signed_at).toLocaleString()} (IP {c.signature_ip || "unknown"})
          </div>
        </div>
      )}

      <div className="bg-[#0a0a0a] border border-white/10 p-6">
        <div className="eyebrow mb-3">Audit trail</div>
        <ul className="text-xs text-white/60 space-y-1" data-testid="contract-audit">
          {(c.audit || []).map((e, i) => (
            <li key={i} className="flex gap-3">
              <span className="text-white/30 shrink-0">{new Date(e.at).toLocaleString()}</span>
              <span className="text-[#D4AF37]">{e.event}</span>
              {e.note && <span className="text-white/50">— {e.note}</span>}
              {e.ip && <span className="text-white/30 ml-auto">{e.ip}</span>}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function stripHtml(html = "") {
  const d = document.createElement("div"); d.innerHTML = html; return d.textContent || "";
}
